import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { productsApi } from '../services/api';
import { useProduct } from '../context/ProductContext';
import { useAuth } from '../context/AuthContext';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Package, Plus, Search, ExternalLink, Tag, Users, Sparkles,
  ArrowRight, Power
} from 'lucide-react';

const emptyForm = {
  name: '',
  product_code: '',
  category: '',
  website_url: '',
  description: '',
  target_audience: '',
  key_benefits: '',
  pain_points_solved: '',
  trial_available: false,
  trial_length_days: '',
  default_funnel: '',
  default_cta: '',
  compliance_text: '',
  is_active: true
};

export default function Products() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { refreshProducts } = useProduct();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await productsApi.list({ search: search || undefined });
      setProducts(res.data || []);
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const reset = () => setForm(emptyForm);

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.product_code?.trim()) {
      toast.error('Product code is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        product_code: form.product_code.trim().toUpperCase().replace(/\s+/g, '_'),
        category: form.category || null,
        website_url: form.website_url || null,
        description: form.description || null,
        target_audience: form.target_audience || null,
        key_benefits: form.key_benefits
          ? form.key_benefits.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
        pain_points_solved: form.pain_points_solved
          ? form.pain_points_solved.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
        trial_available: !!form.trial_available,
        trial_length_days: form.trial_length_days ? parseInt(form.trial_length_days, 10) : null,
        default_funnel: form.default_funnel || null,
        default_cta: form.default_cta || null,
        compliance_text: form.compliance_text || null,
        is_active: !!form.is_active
      };
      await productsApi.create(payload);
      toast.success('Product created');
      setOpen(false);
      reset();
      await load();
      await refreshProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="products-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Products & Services</h1>
          </div>
          <p className="text-slate-500 text-sm">
            Create any product or service you want to promote, follow up, automate, and track.
          </p>
        </div>
        {isAdmin() && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-product-btn" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" /> New Product / Service
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Product / Service</DialogTitle>
                <DialogDescription>
                  Generic fields — no product-specific logic is hardcoded.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 py-2">
                <div>
                  <Label>Name *</Label>
                  <Input
                    data-testid="product-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. AI Lead Automation"
                  />
                </div>
                <div>
                  <Label>Product Code *</Label>
                  <Input
                    data-testid="product-code-input"
                    value={form.product_code}
                    onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                    placeholder="e.g. AI_LEAD_AUTO"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="SaaS, Consulting, Training..."
                  />
                </div>
                <div>
                  <Label>Website URL</Label>
                  <Input
                    value={form.website_url}
                    onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What does this product/service do?"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Target Audience</Label>
                  <Input
                    value={form.target_audience}
                    onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                    placeholder="SMB owners, IT managers, etc."
                  />
                </div>
                <div>
                  <Label>Key Benefits (one per line)</Label>
                  <Textarea
                    rows={3}
                    value={form.key_benefits}
                    onChange={(e) => setForm({ ...form, key_benefits: e.target.value })}
                    placeholder="Save time&#10;Automate follow-ups&#10;Increase conversion"
                  />
                </div>
                <div>
                  <Label>Pain Points Solved (one per line)</Label>
                  <Textarea
                    rows={3}
                    value={form.pain_points_solved}
                    onChange={(e) => setForm({ ...form, pain_points_solved: e.target.value })}
                    placeholder="Manual follow-up&#10;Lead leakage&#10;Slow response time"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.trial_available}
                    onCheckedChange={(v) => setForm({ ...form, trial_available: v })}
                    data-testid="trial-toggle"
                  />
                  <Label>Trial Available</Label>
                </div>
                <div>
                  <Label>Trial Length (days)</Label>
                  <Input
                    type="number"
                    value={form.trial_length_days}
                    disabled={!form.trial_available}
                    onChange={(e) => setForm({ ...form, trial_length_days: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Default Funnel</Label>
                  <Input
                    value={form.default_funnel}
                    onChange={(e) => setForm({ ...form, default_funnel: e.target.value })}
                    placeholder="e.g. Trial → Demo → Close"
                  />
                </div>
                <div>
                  <Label>Default Call-to-Action</Label>
                  <Input
                    value={form.default_cta}
                    onChange={(e) => setForm({ ...form, default_cta: e.target.value })}
                    placeholder="Book a demo / Start free trial"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Compliance / Disclaimer Text</Label>
                  <Textarea
                    rows={2}
                    value={form.compliance_text}
                    onChange={(e) => setForm({ ...form, compliance_text: e.target.value })}
                    placeholder="Any compliance language to include in messaging"
                  />
                </div>
                <div className="flex items-center gap-3 col-span-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="save-product-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {saving ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by name, code, or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="product-search-input"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">Loading...</div>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-700 mb-1">
              No products / services yet
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Create a product or service to start configuring agents, fit rules, and follow-ups.
            </p>
            {isAdmin() && (
              <Button onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" /> Create your first product
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <Card
              key={p.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`product-card-${p.product_code}`}
              onClick={() => navigate(`/products/${p.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate flex items-center gap-2">
                      {p.name}
                      {!p.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      <span className="font-mono">{p.product_code}</span>
                      {p.category && <> · {p.category}</>}
                    </CardDescription>
                  </div>
                  <Package className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {p.description && (
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{p.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {p.trial_available && (
                    <Badge variant="outline" className="text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Trial {p.trial_length_days ? `${p.trial_length_days}d` : ''}
                    </Badge>
                  )}
                  {p.target_audience && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span className="truncate max-w-[140px]">{p.target_audience}</span>
                    </span>
                  )}
                  {p.website_url && (
                    <a
                      href={p.website_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Site
                    </a>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-indigo-600 font-medium">
                  <span>Configure agents & rules</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
